import { Add, Close, ExpandMore, Shuffle } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import DashboardHeader from "../../components/Common/DashboardHeader";
import { getModuleExam, postExamCreation } from "../../services/Exam";
import { ModuleContainer } from "../../style/Admin/AddModule";
import { ExamCreationContainer, StyledButton } from "../../style/Admin/ExamCreationContainer";
import TimeLimitDropdown from "../../components/TimeLimitDropdown";

const QuizCreationPage = () => {
  const location = useLocation();
  const moduleId = location.state?.moduleId ?? "";
  const courseId = location.state?.courseId ?? "";
  const moduleName = location.state?.moduleName ?? "";
  const navigate = useNavigate();

  const [examId, setExamId] = useState(null);
  const [formData, setFormData] = useState({
    quizTitle: moduleName + "_Exam",
    quizDescription: "",
    timeLimit: "",
    passingScore: "",
    questions: [
      {
        question: "",
        options: Array(2).fill(""),
        correctOptionIndex: null,
      },
    ],
  });

  useEffect(() => {
    fetchExamData();
  }, [moduleId]);

  const validateForm = () => {
    if (!formData.quizTitle.trim()) {
      toast.warning("Exam title cannot be empty");
      return false;
    }

    if (!formData.timeLimit) {
      toast.warning("Time limit cannot be empty");
      return false;
    }

    if (!formData.passingScore) {
      toast.warning("Passing score cannot be empty");
      return false;
    }

    if (!formData.quizDescription) {
      toast.warning("Exam description cannot be empty");
      return false;
    }

    for (let i = 0; i < formData.questions.length; i++) {
      const question = formData.questions[i];

      if (!question.question.trim()) {
        toast.warning(`Question ${i + 1} text cannot be empty`);
        return false;
      }

      for (let j = 0; j < question.options.length; j++) {
        if (!question.options[j].trim()) {
          toast.warning(`Option ${j + 1} in Question ${i + 1} cannot be empty`);
          return false;
        }
      }

      if (question.correctOptionIndex === null || question.correctOptionIndex === undefined) {
        toast.warning(`Please select correct option for Question ${i + 1}`);
        return false;
      }
    }

    return true;
  };

  const fetchExamData = async () => {
    try {
      const response = await getModuleExam(moduleId);

      if (!response?.success || !response.exam) {
        return;
      }

      const { exam, questions = [] } = response;
      setExamId(exam.id);

      const finalQuestions = questions.map((q) => ({
        question: q.questionTitle,
        options: Object.values(q.options),
        correctOptionIndex: q.correctOption.charCodeAt(0) - 97,
      }));

      setFormData({
        quizTitle: exam.examTitle || "",
        quizDescription: exam.examDescription || "",
        timeLimit: exam.timeLimit?.toString() || "",
        passingScore: exam.passingMarks?.toString() || "",
        questions: finalQuestions,
      });
    } catch (error) {
      toast.info("Create New Exam");
    }
  };

  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddQuestion = () => {
    setFormData((prev) => {
      prev.questions.push({
        question: "",
        options: Array(2).fill(""),
        correctOptionIndex: null,
      });
      return { ...prev };
    });
  };

  const handleQuestionUpdate = (questionIndex, field, value) => {
    setFormData((prev) => {
      prev.questions[questionIndex][field] = value;
      return { ...prev };
    });
  };

  const handleOptionChange = (questionIndex, optionIndex, newText) => {
    setFormData((prev) => {
      prev.questions[questionIndex].options[optionIndex] = newText;
      return { ...prev };
    });
  };

  const handleDeleteQuestion = (questionIndex) => {
    setFormData((prev) => {
      if (prev.questions.length <= 1) return prev;
      prev.questions.splice(questionIndex, 1);
      return { ...prev };
    });
  };

  const handleDeleteOption = (questionIndex, optionIndex) => {
    setFormData((prev) => {
      const question = prev.questions[questionIndex];

      if (question.options.length <= 2) return prev;

      question.options.splice(optionIndex, 1);
      if (question.correctOptionIndex === optionIndex) {
        question.correctOptionIndex = null;
      } else if (question.correctOptionIndex > optionIndex) {
        question.correctOptionIndex--;
      }

      return { ...prev };
    });
  };

  const handleAddOption = (questionIndex) => {
    setFormData((prev) => {
      prev.questions[questionIndex].options.push("");
      return { ...prev };
    });
  };

  const handleSaveQuiz = async () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      examId,
      courseId,
      moduleId,
      examTitle: formData.quizTitle,
      examDescription: formData.quizDescription,
      passingMarks: Number(formData.passingScore),
      timeLimit: Number(formData.timeLimit),
      questions: formData.questions.map((q) => {
        const optionsObject = {};

        q.options.forEach((text, index) => {
          const key = String.fromCharCode(97 + index);
          optionsObject[key] = text;
        });

        const correctOptionLetter = String.fromCharCode(97 + q.correctOptionIndex);

        return {
          questionTitle: q.question,
          options: optionsObject,
          correctOption: correctOptionLetter,
        };
      }),
    };

    try {
      await postExamCreation(payload);
      toast.success(examId ? "Exam Is Updated" : "Exam Is Created");

      fetchExamData();
    } catch (error) {
      toast.error(error.message || "Failed to save exam");
    }
  };

  const handleInputChange = (field, e) => {
    const newValue = e.target.value.trim();

    if (newValue === "" || /^\d+$/.test(newValue)) {
      let numberValue = newValue === "" ? "" : parseInt(newValue, 10);

      if (numberValue !== "" && numberValue < 0) {
        toast.error("Negative values are not allowed.");
        return;
      }

      if (field === "passingScore") {
        updateFormField(field, numberValue === "" ? "" : Math.min(100, numberValue));
      } else if (field === "timeLimit") {
        updateFormField(field, numberValue);
      } else {
        updateFormField(field, numberValue);
      }
    } else {
      toast.error("Please enter a valid number.");
      updateFormField(field, "");
    }
  };

  const handleShuffleQuestions = () => {
    setFormData((prev) => {
      for (let i = prev.questions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [prev.questions[i], prev.questions[j]] = [prev.questions[j], prev.questions[i]];
      }
      toast.info("Questions have been shuffled");
      return { ...prev };
    });
  };

  const handleShuffleOptions = (questionIndex) => {
    setFormData((prev) => {
      const question = prev.questions[questionIndex];
      const correctOption = question.options[question.correctOptionIndex];

      for (let i = question.options.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [question.options[i], question.options[j]] = [question.options[j], question.options[i]];
      }

      question.correctOptionIndex = question.options.indexOf(correctOption);

      return { ...prev };
    });

    toast.info("Options for this question have been shuffled");
  };

  return (
    <ExamCreationContainer>
      <DashboardHeader title="Exam Creation" />
      <ToastContainer />
      <div className="exam-creation-container">
        <h3 className="quiz-header">Exam Creation</h3>

        <div className="question-settings">
          <div className="form-grid">
            <div>
              <span className="span_lable">Exam Title</span>
              <input placeholder="Title" required value={formData.quizTitle} onChange={(e) => updateFormField("quizTitle", e.target.value)} />
            </div>

            <div>
              <span className="span_lable">Time Limit (in Minutes)</span>
              <TimeLimitDropdown value={formData.timeLimit} onChange={(e) => handleInputChange("timeLimit", e)} />
            </div>

            <div>
              <span className="span_lable">Passing %</span>
              <input
                placeholder="Passing Score (%)"
                type="text"
                required
                value={formData.passingScore}
                onChange={(e) => handleInputChange("passingScore", e)}
                min="0"
                max="100"
              />
            </div>

            <div className="form-grid-full">
              <span className="span_lable">Exam Description</span>
              <textarea
                placeholder="Description"
                value={formData.quizDescription}
                onChange={(e) => updateFormField("quizDescription", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="questions-header">
          <h4>
            Questions
            <span className="question-count">({formData.questions.length})</span>
          </h4>

          <div className="option-flex">
            <StyledButton onClick={handleAddQuestion}>
              <Add /> Add Question
            </StyledButton>
            <StyledButton onClick={handleShuffleQuestions}>
              <Shuffle /> Shuffle Question
            </StyledButton>
          </div>
        </div>

        {formData.questions.map((question, questionIndex) => (
          <ModuleContainer key={questionIndex}>
            <Accordion className="module-accordion ">
              <AccordionSummary className="module-header" expandIcon={<ExpandMore />}>
                <div className="question-header">
                  <h5 className="question-title">Question {questionIndex + 1}</h5>
                </div>
              </AccordionSummary>
              <AccordionDetails className="module-details ">
                <div className="question-content">
                  <div className="flex-input">
                    <input
                      placeholder="Question Text"
                      required
                      value={question.question}
                      onChange={(e) => handleQuestionUpdate(questionIndex, "question", e.target.value)}
                      className="question-text"
                    />
                    <div className="action_button">
                      <StyledButton onClick={() => handleDeleteQuestion(questionIndex)} disabled={formData.questions.length <= 1}>
                        <Close className="close-btn" /> Question
                      </StyledButton>
                      <StyledButton onClick={() => handleAddOption(questionIndex)}>
                        <Add /> Add Option
                      </StyledButton>
                      <StyledButton onClick={() => handleShuffleOptions(questionIndex)}>
                        <Shuffle /> Shuffle Options
                      </StyledButton>
                    </div>
                  </div>

                  <h6 className="options-title">Options (select the correct answer)</h6>

                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={question.correctOptionIndex !== null ? question.correctOptionIndex : ""}
                      onChange={(e) => {
                        handleQuestionUpdate(questionIndex, "correctOptionIndex", parseInt(e.target.value));
                      }}
                    >
                      {question.options.map((optionText, optionIndex) => (
                        <div key={optionIndex} className="option-item ">
                          <FormControlLabel value={optionIndex} control={<Radio className="primary-radio" />} label="" />
                          <input
                            placeholder={`Option ${optionIndex + 1}${optionIndex === question.correctOptionIndex ? " (Correct)" : ""}`}
                            required
                            value={optionText}
                            onChange={(e) => handleOptionChange(questionIndex, optionIndex, e.target.value)}
                          />
                          <StyledButton
                            className="close-btn font12 deleteIcon"
                            onClick={() => handleDeleteOption(questionIndex, optionIndex)}
                            disabled={question.options.length <= 2}
                          >
                            <Close className="close-btn" />
                          </StyledButton>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </div>
              </AccordionDetails>
            </Accordion>
          </ModuleContainer>
        ))}

        <div className="footer-actions">
          <StyledButton onClick={() => navigate(-1)}>Cancel</StyledButton>
          <StyledButton onClick={handleSaveQuiz}>Save as Draft</StyledButton>
        </div>
      </div>
    </ExamCreationContainer>
  );
};

export default QuizCreationPage;
